<script setup lang="ts">
import { useGlobalCounter } from '@/composables/useGlobalCounter';
import Message from 'primevue/message';
import Select from 'primevue/select';
import { computed, useAttrs, type Ref } from 'vue';

defineOptions({
    // disable automatic attr inheritance: we do not want to pass attr to the root element,
    // but to the inner field instead
    inheritAttrs: false,
});

export type KpSelectFieldProps = {
    label?: string | null;
    inputId?: string;
    inputClass?: string | object;
    loading?: boolean;
    error?: string | null;
};

const value: Ref<any> = defineModel();
const props = withDefaults(defineProps<KpSelectFieldProps>(), {
    inputId: () => useGlobalCounter('field'),
    inputClass: '',
    error: '',
    loading: false,
});

const cls = useAttrs().class || null;
const fieldAttrs = computed(() => {
    const returnObj = { ...useAttrs() };
    // Remove attributes for the inner field we do NOT want to pass along:
    delete returnObj.id;
    delete returnObj.class;
    delete returnObj.label;
    return returnObj;
});

const emit = defineEmits<{
    (e: 'change', value: any): void;
}>();

const slots = defineSlots<{
    [key: string]: (slotProps: any) => any;
}>();

function onChange(value: any) {
    emit('change', value);
}
</script>

<template>
    <div :class="`field ${cls}`">
        <label v-if="label" :for="inputId" v-html="label"></label>
        <Select v-model="value" :class="inputClass" :loading="loading" v-bind="fieldAttrs" @value-change="onChange">
            <!-- expose all child slots of Select: -->
            <template v-for="(name, index) in Object.keys($slots)" :key="index" #[name]="slotProps">
                <slot :name="name" :slotProps="slotProps"></slot>
            </template>
        </Select>
        <Message v-if="error" class="error" size="small" severity="error" variant="simple">{{ error }}</Message>
    </div>
</template>

<style lang="css" scoped>
.error {
    --p-message-text-font-weight: normal;
}
</style>
